import React from 'react';
import './timeline.styles.css'; // Importing the CSS file stylesheet from above

const TIMELINE_DATA = [
  { year: '2027', month: 'Present', type: 'empty' },
  {
    year: '2026',
    month: 'June',
    type: 'experience',
    position: 'left',
    title: 'Senior Engineer',
    company: 'Reltio (now SAP)',
    className: 'block-reltio-green',
    dateRange: 'Dec 2024 - Present',
    bullets: [
      'Architected distributed Java services to automate tenant provisioning for a large-scale MDM platform, reducing environment setup time from several hours to under 40–50 minutes.',
      'Designed and owned a multi-tenant lifecycle management system enabling on-demand creation, updates, and teardown of isolated environments, significantly reducing operational overhead.',
      'Built GCP Cloud Functions for free-tier tenant self-serve provisioning, enabling external developer onboarding without manual intervention.',
      'Engineered bidirectional data replication between Google Cloud Spanner and Azure Cosmos DB, ensuring consistency across cloud boundaries for multi-tenant environments.',
      'Designed industry-specific MDM templates for insurance and healthcare verticals, accelerating enterprise customer go-live timelines.'
    ]
  },
  { year: '2025', month: 'Jan', type: 'empty' },
  {
    year: '2024',
    month: 'Dec',
    type: 'experience',
    position: 'left',
    title: 'Senior Software Engineer',
    company: 'OpenText',
    className: 'block-opentext-red',
    dateRange: 'Aug 2023 - Dec 2024',
    bullets: [
      'Owned end-to-end quarterly releases for five enterprise products, including build orchestration and security vulnerability scanning (Fortify, Black Duck), in close collaboration with platform, QA, and DevOps teams.',
      'Migrated a legacy on-premise product to cloud infrastructure, managing RMI services within Tomcat and designing a cloud-agnostic deployment architecture supporting multiple cloud providers.',
      'Implemented centralized logging pipeline using Fluentd to forward application logs to New Relic dashboards, enabling proactive monitoring, failure detection, and automated alerting – improving incident response time.'
    ]
  },
  {
    year: '2023',
    month: 'Aug',
    type: 'experience',
    position: 'left',
    title: 'Software Engineer',
    company: 'OpenText',
    className: 'block-opentext-purple',
    dateRange: 'Oct 2021 - Aug 2023',
    bullets: [
      'Built Python automation tooling to upgrade Java dependencies across 5+ large monolithic enterprise products, significantly reducing manual upgrade effort.',
      'Led platform modernization of Documentum by upgrading to Java 17, Spring Framework 6, and Tomcat 10 – improving runtime stability and security posture.',
      'Migrated .NET and C++ projects from Visual Studio 2013 to 2019, resolving build dependencies and enabling modern toolchain support.',
      'Implemented centralized logging via Graylog and added root squash / non-root squash support in cloud deployments, enhancing security and operational visibility.'
    ]
  },
  { year: '2022', month: 'Jan', type: 'empty' },
  {
    year: '2021',
    month: 'Sep',
    type: 'experience',
    position: 'left',
    title: 'Associate Software Engineer',
    company: 'OpenText',
    className: 'block-opentext-blue',
    dateRange: 'Aug 2019 - Sep 2021',
    bullets: [
      'Contributed to Documentum Composer (Eclipse RCP), delivering feature improvements used by enterprise clients worldwide.',
      'Executed Java 8 to Java 11 runtime upgrade across critical platform components, improving performance and enabling modern Java feature adoption.',
      'Collaborated with senior engineers on production issue triage, developing strong debugging and root-cause analysis skills.'
    ]
  },
  { year: '2020', month: 'Jan', type: 'empty' },
  {
    year: '2019',
    month: 'Pivot',
    type: 'parallel',
    leftCard: {
      title: 'Software Engineering Intern',
      company: 'OpenText',
      className: 'block-opentext-intern-green',
      dateRange: 'Mar 2019 - Aug 2019',
      bullets: ['Built HTML form generation automation as part of an Integrated Workflow Management application, reducing manual form creation effort for the engineering team.']
    },
    rightCard: {
      title: 'Bachelor of Engineering – Computer Science',
      company: 'Mahakal Institute of Technology',
      className: 'block-education-blue',
      dateRange: 'Aug 2015 - May 2019',
      bullets: ['Ujjain, India — Core coursework focused on foundational computer science paradigms, algorithms, data structures, and distributed software engineering infrastructure.']
    }
  },
  { year: '2015', month: 'Aug', type: 'empty' }
];

const TimelineCard = ({ title, company, bullets, dateRange, className }) => (
  <div className={`beautiful-timeline-item ${className}`}>
    <div>
      <h3 className="beautiful-timeline-item__title">{title}</h3>
      {company && <h4 className="beautiful-timeline-item__subtitle">{company}</h4>}
      <ul className="beautiful-timeline-item__bullets">
        {bullets.map((bullet, idx) => (
          <li key={idx}>{bullet}</li>
        ))}
      </ul>
    </div>
    <div className="beautiful-timeline-item__footer">{dateRange}</div>
  </div>
);

export default function BeautifulTimeline() {
  return (
    <div className="beautiful-timeline-container">
      <div className="beautiful-timeline">
        
        <div className="beautiful-timeline__spine" />

        {TIMELINE_DATA.map((row, index) => {
          const isParallel = row.type === 'parallel';
          const rowClass = `beautiful-timeline__row ${isParallel ? 'beautiful-timeline__row--parallel' : ''}`;

          return (
            <div key={index} className={rowClass}>
              
              {/* Left Column Placement Slot */}
              <div className="beautiful-timeline__cell beautiful-timeline__cell--left">
                {row.type === 'experience' && row.position === 'left' && (
                  <TimelineCard {...row} />
                )}
                {isParallel && <TimelineCard {...row.leftCard} />}
              </div>

              {/* Center Axis Date Badge Components */}
              <div className="beautiful-timeline__cell beautiful-timeline__cell--center">
                <div className="axis-node">
                  <span className="axis-node__year">{row.year}</span>
                  <span className="axis-node__month">{row.month}</span>
                </div>
              </div>

              {/* Right Column Placement Slot */}
              <div className="beautiful-timeline__cell beautiful-timeline__cell--right">
                {row.type === 'experience' && row.position === 'right' && (
                  <TimelineCard {...row} />
                )}
                {isParallel && <TimelineCard {...row.rightCard} />}
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}